<template>
  <el-card v-loading="data_loading">
    <div class="data-title flex-space-between" slot="header">
      <p>Tracks</p>
      <TracksFilters />
      <TrackDialog />
    </div>
    <el-table
      :data="track_data"
      :default-sort="{ prop: order_column, order: order_type }"
      @sort-change="sort"
      class="w100"
      @expand-change="expand"
      stripe
      :row-class-name="tableRowClassName"
      v-loading="loading"
    >
      <el-table-column type="expand" label="Details" width="100%">
        <template slot-scope="props">
          <div v-if="props.row.keywords" class="inline">
            <strong>Keywords</strong>
            <p>{{ props.row.keywords }}</p>
          </div>
          <div v-loading="report_loading">
            <div v-if="!report_loading">
              <div class="inline">
                <BarChart :chart-data="report.previews"></BarChart>
                <strong>Sum: {{ report.previews.sum }}</strong>
              </div>
              <div class="inline">
                <BarChart :chart-data="report.downloads"></BarChart>
                <strong>Sum: {{ report.downloads.sum }}</strong>
              </div>
              <div class="inline">
                <BarChart :chart-data="report.previews_territories"></BarChart>
                <strong>Sum: {{ report.previews.sum }}</strong>
              </div>
              <div class="inline">
                <BarChart :chart-data="report.downloads_territories"></BarChart>
                <strong>Sum: {{ report.downloads.sum }}</strong>
              </div>
            </div>
          </div>
          <div>
            <br /><br />
            <p><strong>Views by channel</strong></p>
            <el-table
              :data="report.downloads_by_channel"
              stripe
              border
              style="width:501px"
            >
              <el-table-column
                label="Channel ID"
                prop="channel_id"
                width="300%"
              >
                <template slot-scope="props">
                  <a
                    :href="
                      `https://youtube.com/channel/${props.row.channel_id}`
                    "
                    target="_blank"
                  >
                    {{ props.row.channel_id }}
                  </a>
                </template>
              </el-table-column>
              <el-table-column
                label="Channel view count"
                prop="view_count"
                align="right"
                width="200%"
              ></el-table-column>
            </el-table>
          </div>
        </template>
      </el-table-column>
      <el-table-column width="100%">
        <template slot-scope="props">
          <img
            v-if="props.row.image_url"
            class="avatar"
            :src="props.row.image_url"
          />
          <img
            v-else
            class="avatar"
            :src="`https://app.syncvault.com/static/apple-touch-icon.png`"
          />
        </template>
      </el-table-column>
      <el-table-column
        label="Title"
        prop="title"
        sortable="custom"
        width="300%"
      ></el-table-column>
      <el-table-column
        label="Artist"
        prop="artist_name"
        sortable="custom"
        width="180%"
      ></el-table-column>
      <el-table-column
        label="Song length"
        prop="song_length"
        sortable="custom"
        width="140%"
      ></el-table-column>
      <el-table-column label="Start date" prop="date_start" sortable="custom">
        <template slot-scope="props">
          {{ props.row.date_start | date }}
        </template>
      </el-table-column>
      <el-table-column label="End date" prop="date_end" sortable="custom">
        <template slot-scope="props">
          {{ props.row.date_end | date }}
        </template>
      </el-table-column>
      <el-table-column
        label="Published date"
        prop="published_at_utc"
        sortable="custom"
      >
        <template slot-scope="props">
          {{ props.row.published_at_utc | date }}
        </template>
      </el-table-column>
      <el-table-column label="Actions" align="right">
        <template slot-scope="props">
          <el-tooltip content="Open on SyncVault">
            <el-button
              type="success"
              circle
              size="small"
              icon="el-icon-zoom-in"
              @click="openSB(props.row.artist_id)"
            ></el-button>
          </el-tooltip>
          <el-tooltip
            content="Publish"
            v-if="props.row.published_at_utc == null && level3"
          >
            <el-button
              type="success"
              circle
              size="small"
              icon="el-icon-top"
              @click="publish(props.row.id)"
            ></el-button>
          </el-tooltip>
          <el-tooltip
            content="Unpublish"
            v-if="props.row.published_at_utc !== null && level3"
          >
            <el-button
              type="danger"
              circle
              size="small"
              icon="el-icon-bottom"
              @click="unpublish(props.row.id)"
            ></el-button>
          </el-tooltip>
          <el-tooltip content="Edit" v-if="level3">
            <el-button
              type="warning"
              circle
              size="small"
              icon="el-icon-edit"
              @click="openEdit(props.row)"
            ></el-button>
          </el-tooltip>
          <el-tooltip
            v-if="props.row.vc_campaign > 0 || haveSocials(props.row.socials)"
            content="Campaign reports"
          >
            <el-button
              type="primary"
              circle
              size="small"
              icon="el-icon-document"
              @click="openDialog(props.row.id, props.row.socials)"
            ></el-button>
          </el-tooltip>
        </template>
      </el-table-column>
    </el-table>
    <el-pagination
      class="float-right"
      background
      @size-change="sizeChange"
      @current-change="currentChange"
      :current-page.sync="tracks.current_page"
      :page-sizes="[5, 10, 25, 50, 100]"
      :page-size="page_size"
      layout="total, sizes, prev, pager, next, jumper"
      :total="tracks.total"
    ></el-pagination>
    <vue-html2pdf
      :show-layout="false"
      :float-layout="true"
      :enable-download="true"
      :preview-modal="false"
      :paginate-elements-by-height="1122"
      filename="PDF Reports"
      :pdf-quality="2"
      :manual-pagination="true"
      pdf-format="a4"
      pdf-orientation="portrait"
      pdf-content-width="795px"
      :html-to-pdf-options="htmlToPdfOptions"
      ref="html2PdfNew"
    >
      <section slot="pdf-content">
        <VCPdfReportNew :pd="publish_date" />
      </section>
    </vue-html2pdf>
    <vue-html2pdf
      :show-layout="false"
      :float-layout="true"
      :enable-download="true"
      :preview-modal="false"
      :paginate-elements-by-height="1122"
      filename="PDF Reports"
      :pdf-quality="2"
      :manual-pagination="true"
      pdf-format="a4"
      pdf-orientation="portrait"
      pdf-content-width="795px"
      :html-to-pdf-options="htmlToPdfOptions"
      ref="html2PdfChallenge"
    >
      <section slot="pdf-content">
        <ReportChallenge
          type="track"
          :pdf_report="campaign_pdf_report"
          :challenge_type="challenge_type"
          :countries_chart="countries_chart"
          :chart_pdf="PieChartSettPdf"
          :pdc="publish_date_challenge"
        />
      </section>
    </vue-html2pdf>
    <el-dialog
      title="Campaign dates"
      :visible="dialog"
      width="50%"
      top="3vh"
      :modal="true"
      :append-to-body="true"
      @close="dialog = false"
      @open="openModal"
    >
      <div>
        <div class="flex-space-around">
          <div class="align-center" v-for="(i, id) in campaign_track" :key="id">
            <p>
              <strong>Start:</strong>
              {{ i.campaign_track_start | date }}
            </p>
            <p>
              <strong>End:</strong>
              {{ i.campaign_track_end | date }}
            </p>
            <p>
              <el-checkbox label="Guaranteed Payment" v-model="guaranteed_payment"></el-checkbox>
            </p>
            <p>
              <el-checkbox label="Shout Out" v-model="shoutout"></el-checkbox>
            </p>
            <p>
              <el-checkbox label="Publish date" v-model="publish_date"></el-checkbox>
            </p>
<!--            <el-button
              type="primary"
              icon="el-icon-document"
              @click="generateReport(i.id)"
              class="mb10"
              >Download report</el-button
            >-->
            <el-button
              type="primary"
              icon="el-icon-document"
              @click="generateNewReport(i.id)"
              class="mb10"
            >Download report</el-button
            >
          </div>
          <div
            v-if="shortsButton || tiktokButton || instagramButton"
            class="challenge_reports">
            <el-button
              type="primary"
              icon="el-icon-document"
              @click="generateShortsReport(track_id)"
              class="mb10"
              v-if="shortsButton"
            >Download Shorts report
            </el-button>
            <el-button
              type="primary"
              icon="el-icon-document"
              @click="generateTikTokReport(track_id)"
              class="mb10"
              v-if="tiktokButton"
            >Download TikTok report
            </el-button>
            <el-button
              type="primary"
              icon="el-icon-document"
              @click="generateInstagramReport(track_id)"
              class="mb10"
              v-if="instagramButton"
            >Download Instagram report
            </el-button>

            <p>
              <el-checkbox label="Publish date" v-model="publish_date_challenge"></el-checkbox>
            </p>
          </div>
        </div>
      </div>
    </el-dialog>
  </el-card>
</template>

<script>
import { mapState, mapActions, mapMutations, mapGetters } from 'vuex'
import BarChart from '@/global/components/charts/Bar'
import TracksFilters from '../components/TracksFilters'
import TrackDialog from '../components/TrackDialog'
import VueHtml2pdf from 'vue-html2pdf'
import VCPdfReportNew from './VCPdfReportNew'
import ReportChallenge from "../../shared/VCPdfReportChallenge"
import axios from '@/plugins/axios';
import store from "../../../../plugins/vuex";
import isNull from "lodash/isNull";

export default {
  components: {
    TracksFilters,
    TrackDialog,
    BarChart,
    VueHtml2pdf,
    VCPdfReportNew,
    ReportChallenge
  },

  data() {
    return {
      dialog: false,
      shortsButton:false,
      tiktokButton:false,
      instagramButton:false,
      track_id: 0,
      htmlToPdfOptions: {
        image: {
          type: 'jpeg',
          quality: 0.98
        },
        enableLinks: true,
        html2canvas: {
          dpi: 192,
          scale:4,
          letterRendering: true,
          useCORS: true
        },
        jsPDF: {
          unit: 'in',
          format: 'a4',
          orientation: 'portrait'
        },
        filename: 'file.pdf'
      },
      loading: false,
      guaranteed_payment: 0,
      shoutout: 0,
      publish_date: 0,
      publish_date_challenge: 0
    }
  },

  computed: {
    ...mapState('newReleasesTracks', [
      'tracks',
      'report',
      'page',
      'page_size',
      'order_column',
      'order_type',
      'date_range',
      'data_loading',
      'report_loading',
      'campaign_id',
      'pdf_report',
      'campaign_pdf_report',
      'campaign_track',
      'pricing',
      'challenge_type',
      'PieChartSettPdf'
    ]),
    ...mapGetters('newReleasesTracks', ['track_campaigns', 'track_data','countries_chart']),
    level3: function() {
      if (store.state.auth.user.permission === 3) return true
      else {
        return false
      }
    }
  },

  methods: {
    ...mapActions('newReleasesTracks', [
      'read',
      'readReport',
      'readPdfReport',
      'readShortsReport',
      'readTikTokReport',
      'readInstagramReport',
      'publishTrack',
      'unpublishTrack',
      'readCampaignTrack'
    ]),
    ...mapActions('trackImport', ['readArtist']),
    ...mapMutations('newReleasesTracks', [
      'setTrack',
      'setPage',
      'setPageSize',
      'setOrdering',
      'setDialog',
      'setTrackImage',
      'setCampaignId',
      'setMoods',
      'setGenres',
      'setRestrictions',
      'setSocials',
      'setPricing'
    ]),

    openSB(id) {
      window.open(`https://syncvault.com/artist/${id}`, '_blank')
    },
    openCreate() {
      this.setDialog({ status: true, type: 'Create' })
      this.setTrack({})
      this.setTrackImage(null)
    },
    openEdit(track) {
      track = JSON.parse(JSON.stringify(track))
      this.setDialog({ status: true, type: 'Edit' })
      track.restrictions = track.restrictions.map(r => r.name)
      track.moods = track.moods.map(m => m.id)
      track.track_genres = track.track_genres.map(tg => tg.id)
      track.socials = track.socials.map(s => s.id)
      track.tiktok_pricing =  this.pricing.map(obj1 => {
        const obj2 = track.tiktok_challenge ? track.tiktok_challenge.pricing.find(obj2 => obj2.pricing_id === obj1.id) :{};
        return {
          id: obj1.id,
          threshold: obj1.threshold,
          payment: obj2 ? obj2.payment : null
        }
      });
      track.instagram_pricing =  this.pricing.map(obj1 => {
        const obj2 = track.instagram_challenge ? track.instagram_challenge.pricing.find(obj2 => obj2.pricing_id === obj1.id) :{};
        return {
          id: obj1.id,
          threshold: obj1.threshold,
          payment: obj2 ? obj2.payment : null
        }
      });
      track.short_pricing =  this.pricing.map(obj1 => {
        const obj2 = track.short_challenge ? track.short_challenge.pricing.find(obj2 => obj2.pricing_id === obj1.id): {};
        return {
          id: obj1.id,
          threshold: obj1.threshold,
          payment: obj2 ? obj2.payment : null
        }
      });

      const challengeTypes = ['short_challenge', 'tiktok_challenge', 'instagram_challenge', 'regular_challenge'];

      challengeTypes.forEach(challengeType => {
        if (isNull(track[challengeType])) {
          track[challengeType] = {};
        } else {
          const challenge = track[challengeType];

          if (challenge.budget > 0) challenge.budget = challenge.budget / 10;
          if (challenge.guaranteed_payment > 0) challenge.guaranteed_payment = challenge.guaranteed_payment / 10;
          if (challenge.bonus_payment > 0) challenge.bonus_payment = challenge.bonus_payment / 10;
          if (challenge.shoutout_payment > 0) challenge.shoutout_payment = challenge.shoutout_payment / 10;
        }
      });

      this.setTrack(track)
      this.setTrackImage(track.image_url)
    },
    sizeChange(page_size) {
      this.setPageSize(page_size)
      this.read()
    },
    currentChange(page) {
      this.setPage(page)
      this.read()
    },
    sort(d) {
      this.setOrdering({
        column: d.prop,
        type: this.ascDesc(d.order)
      })
      this.read()
    },
    expand(row, expanded) {
      // nasty hack -> close currently expanded row when another is opened
      if (expanded.length === 0) return
      const currentExpandedIcon = document.querySelector(
        '.el-table__expand-icon.el-table__expand-icon--expanded'
      )
      if (currentExpandedIcon) currentExpandedIcon.click()

      if (expanded.some(e => e.id === row.id)) {
        this.readReport(row.id)
      }
    },
    tableRowClassName(data) {
      if (data.row.featured) return 'featured'
      return ''
    },
    publish(id) {
      this.$confirm('You will publish this track. Continue?', 'Warning', {
        confirmButtonText: 'OK',
        cancelButtonText: 'Cancel',
        type: 'warning'
      })
        .then(() => {
          this.publishTrack(id)
        })
        .catch(() => {
          this.$message({
            type: 'info',
            message: 'Publish cancelled'
          })
        })
    },
    unpublish(id) {
      this.$confirm('You will unpublish this track. Continue?', 'Warning', {
        confirmButtonText: 'OK',
        cancelButtonText: 'Cancel',
        type: 'warning'
      })
        .then(() => {
          this.unpublishTrack(id)
        })
        .catch(() => {
          this.$message({
            type: 'info',
            message: 'Unpublish cancelled'
          })
        })
    },
    openDialog(id, socials) {
      this.readCampaignTrack(id)
      this.dialog = true
      this.track_id = id
      this.shortsButton = !!socials.find(s => s.name === 'YouTube Short')
      this.tiktokButton = !!socials.find(s => s.name === 'TikTok')
      this.instagramButton = !!socials.find(s => s.name === 'Instagram')
    },
    openModal() {
      this.guaranteed_payment = 0
      this.shoutout = 0
      this.publish_date = 0
    },
    generateReport(id) {
      this.loading = true
      let send = {
        id: id,
        guaranteed_payment: this.guaranteed_payment?1:0,
        shoutout: this.shoutout?1:0,
        publish_date: this.publish_date?1:0
      }
      this.readPdfReport(send).then(() => {
        let filename = this.pdf_report.artist + '_' + this.pdf_report.track + '_YouTubeRegular'
        filename = filename.replace(/\s/g, '_')
        filename = filename.toLowerCase()
        this.htmlToPdfOptions.filename = filename + '.pdf'
        setTimeout(() => {
          this.$refs.html2Pdf.generatePdf()
          this.loading = false
        }, 1000)
      })
    },
    generateNewReport(id) {
      this.loading = true
      let send = {
        id: id,
        guaranteed_payment: this.guaranteed_payment?1:0,
        shoutout: this.shoutout?1:0,
        publish_date: this.publish_date?1:0
      }
      this.readPdfReport(send).then(() => {
        let filename = this.pdf_report.artist + '_' + this.pdf_report.track + '_YouTubeRegular'
        filename = filename.replace(/\s/g, '_')
        //filename = filename.toLowerCase()
        this.htmlToPdfOptions.filename = filename + '.pdf'
        setTimeout(() => {
          this.$refs.html2PdfNew.generatePdf()
          this.loading = false
        }, 1000)
      })
    },
    generateShortsReport(id) {
      this.loading = true
      let send = {
        id: id,
      }
      this.readShortsReport(send).then(() => {
        let filename = this.campaign_pdf_report.music_performer + '_' + this.campaign_pdf_report.music_title + '_Shorts'
        filename = filename.replace(/\s/g, '_')
        //filename = filename.toLowerCase()
        this.htmlToPdfOptions.filename = filename + '.pdf'
        setTimeout(() => {
          this.$refs.html2PdfChallenge.generatePdf()
          this.loading = false
        }, 1000)
      })
    },
    generateTikTokReport(id) {
      this.loading = true
      let send = {
        id: id,
      }
      this.readTikTokReport(send).then(() => {
        let filename = this.campaign_pdf_report.music_performer + '_' + this.campaign_pdf_report.music_title + '_TikTok'
        filename = filename.replace(/\s/g, '_')
        //filename = filename.toLowerCase()
        this.htmlToPdfOptions.filename = filename + '.pdf'
        setTimeout(() => {
          this.$refs.html2PdfChallenge.generatePdf()
          this.loading = false
        }, 1000)
      })
    },
    generateInstagramReport(id) {
      this.loading = true
      let send = {
        id: id,
      }
      this.readInstagramReport(send).then(() => {
        let filename = this.campaign_pdf_report.music_performer + '_' + this.campaign_pdf_report.music_title + '_Instagram'
        filename = filename.replace(/\s/g, '_')
        //filename = filename.toLowerCase()
        this.htmlToPdfOptions.filename = filename + '.pdf'
        setTimeout(() => {
          this.$refs.html2PdfChallenge.generatePdf()
          this.loading = false
        }, 1000)
      })
    },
    getValuesFromCollectionForSpecificKey(collections, key) {
      let arr = []
      collections.forEach(function(item) {
        arr.push(item[key])
      })
      return arr
    },
    haveSocials(socials){
      const names = ['TikTok', 'Instagram', 'YouTube Short'];

      if(socials.length >0){
        return socials.some(social => names.includes(social.name));
      }
      return false
    }
  },

  async created() {
    this.read()
    this.readArtist()

    let genres = await axios.api.get('/genres')
    if (genres.data.success) this.setGenres(genres.data.genres)

    let moods = await axios.api.get('/moods')
    if (moods.data.success) this.setMoods(moods.data.moods)

    let restrictions = await axios.api.get('/restrictions')
    if (restrictions.data.success) {
      this.setRestrictions(
          restrictions.data.restrictions.map(r => r.name)
      )
    }
    let { data } = await axios.api.get('/socials')
    if (data) this.setSocials(data)

    let pricing = await axios.api.get('/challenge/pricing')
    if (pricing.data.success) {
      pricing.data.pricing.forEach(obj => {
        obj.payment = null; //added payment field for dialog form
      });
      this.setPricing(
        pricing.data.pricing
      )
    }
  }
}
</script>
<style scoped lang="scss">
.challenge_reports{
  display: flex;
  flex-direction: column;
  justify-content: center;
  :first-child {
    margin-left: 10px;
  }
}
</style>
